import type { IAccordionItem } from "~/aihd-base-nuxt/types/components/accordion.type";
import type { IAboutService, IExploreItems, IHero, IImageSlider, IVSHero } from "~/types/components/landings.type";

export const useItemRemovalContent = () => {
    const { t } = useI18n();
    const auth = useAuth();
    const { isDesktop } = useDevice();

    const heroContent: IVSHero = {
        title: t("pages_IR_hero_title"),
        title_span: t("pages_IR_hero_orange_title"),
        tag: t("pages_IR_hero_tag"),
        features: [
            {
                icon: "currency-dollar",
                text: t("pages_IR_hero_feature_1"),
            },
            {
                icon: "infinity",
                text: t("pages_IR_hero_feature_2"),
            },
            {
                icon: "list",
                text: t("pages_IR_hero_feature_3"),
            },
            {
                icon: "currency-dollar",
                text: t("pages_IR_hero_feature_4"),
            },
        ],
        cta: {
            text: t("pages_IR_hero_cta_text"),
            link: "/dashboard/upload-photo/service-item-removal",
            id: "itemremoval-herosection-letsgo-button",
        },
        slides: [
            {
                avatar_src: "/Landings/ir/Hero/heroir_pic_img1.webp",
                logo_src: "/Landings/ir/Hero/heroir_logo_img1.webp",
                image_src: "/Landings/ir/Hero/ir-landing-hero1.webp",
                alt: "item removal example",
                title: "Dan Safier",
                subtitle: t("pages_IR_hero_slide_1_subtitle"),
                description: t("pages_IR_hero_slide_1_description"),
                last_sentence: t("pages_IR_hero_slide_1_last_sentence"),
            },
            {
                avatar_src: "/Landings/ir/Hero/heroir_pic_img2.webp",
                logo_src: "/Landings/ir/Hero/heroir_logo_img2.webp",
                image_src: "/Landings/ir/Hero/ir-landing-hero2.webp",
                alt: "real estate item removal",
                title: "Lily Vallario",
                subtitle: t("pages_IR_hero_slide_2_subtitle"),
                description: t("pages_IR_hero_slide_2_description"),
                last_sentence: t("pages_IR_hero_slide_2_last_sentence"),
            },
            {
                avatar_src: "/Landings/ir/Hero/heroir_pic_img3.webp",
                logo_src: "/Landings/ir/Hero/heroir_logo_img3.webp",
                image_src: "/Landings/ir/Hero/ir-landing-hero3.webp",
                alt: "item removal before and after",
                title: "Natalie",
                subtitle: t("pages_IR_hero_slide_3_subtitle"),
                description: t("pages_IR_hero_slide_3_description"),
            },
        ],
    };

    const optionSection = [
        {
            title: t("pages_IR_option_1_title"),
            subtitle: t("pages_IR_option_1_subtitle"),
            slides: {
                after: {
                    src: "https://revi.rvstatic.com/aihomedesign/images-v3/Landings/ir/ir-a.webp",
                },
                before: {
                    src: "https://revi.rvstatic.com/aihomedesign/images-v3/Landings/ir/ir-b.webp",
                },
            },
        },
        {
            title: t("pages_IR_option_2_title"),
            subtitle: t("pages_IR_option_2_subtitle"),
            image_src: "https://revi.rvstatic.com/aihomedesign/images-v3/Landings/ir/irmask.webp",
        },
    ];
    const compareSlider: IImageSlider = {
        title: t("pages_IR_in_action_title"),
        slides: [
            {
                after: {
                    src: `https://revi.rvstatic.com/aihomedesign/images-v3/Landings/ir/ir-1-a${
                        isDesktop ? "" : "-s"
                    }.webp`,
                },
                before: {
                    src: `https://revi.rvstatic.com/aihomedesign/images-v3/Landings/ir/ir-1-b${
                        isDesktop ? "" : "-s"
                    }.webp`,
                },
            },
            {
                after: {
                    src: `https://revi.rvstatic.com/aihomedesign/images-v3/Landings/ir/ir-2-a${
                        isDesktop ? "" : "-s"
                    }.webp`,
                },
                before: {
                    src: `https://revi.rvstatic.com/aihomedesign/images-v3/Landings/ir/ir-2-b${
                        isDesktop ? "" : "-s"
                    }.webp`,
                },
            },
            {
                after: {
                    src: `https://revi.rvstatic.com/aihomedesign/images-v3/Landings/ir/ir-3-a${
                        isDesktop ? "" : "-s"
                    }.webp`,
                },
                before: {
                    src: `https://revi.rvstatic.com/aihomedesign/images-v3/Landings/ir/ir-3-b${
                        isDesktop ? "" : "-s"
                    }.webp`,
                },
            },
            {
                after: {
                    src: `https://revi.rvstatic.com/aihomedesign/images-v3/Landings/ir/ir-4-a${
                        isDesktop ? "" : "-s"
                    }.webp`,
                },
                before: {
                    src: `https://revi.rvstatic.com/aihomedesign/images-v3/Landings/ir/ir-4-b${
                        isDesktop ? "" : "-s"
                    }.webp`,
                },
            },
            {
                after: {
                    src: `https://revi.rvstatic.com/aihomedesign/images-v3/Landings/ir/ir-5-a${
                        isDesktop ? "" : "-s"
                    }.webp`,
                },
                before: {
                    src: `https://revi.rvstatic.com/aihomedesign/images-v3/Landings/ir/ir-5-b${
                        isDesktop ? "" : "-s"
                    }.webp`,
                },
            },
        ],
    };

    const itemRemovalSlides: IImageSlider = {
        title: t("global_serviceName_itemRemoval"),
        subtitle: t("global_serviceName_itemRemoval_desc"),
        slides: [
            {
                after: {
                    src: "/Photo-Editing/IR/Large/01-IR-After.webp",
                },
                before: {
                    src: "/Photo-Editing/IR/Large/01-IR-Before.webp",
                },
            },
            {
                after: {
                    src: "/Photo-Editing/IR/Large/02-IR-After.webp",
                },
                before: {
                    src: "/Photo-Editing/IR/Large/02-IR-Before.webp",
                },
            },
            {
                after: {
                    src: "/Photo-Editing/IR/Large/03-IR-After.webp",
                },
                before: {
                    src: "/Photo-Editing/IR/Large/03-IR-Before.webp",
                },
            },
        ],

        cta: [
            {
                link: "/dashboard/upload-photo/service-item-removal",
                type: "real-estate-hub",
                text: t(auth.value ? "global_letsGo" : "global_tryForFree"),
                id: "item-removal-itemremovalsection-letsgo-button",
            },
        ],
    };

    const aboutItems: IExploreItems[] = [
        {
            text: t("global_serviceName_virtualStaging"),
            link: "/virtual-staging",
            image: {
                src: "https://revi.rvstatic.com/aihomedesign/images-v3/Landings/general/tools-VS.webp",
            },
            id: "itemremoval-othertoolsection-vscard",
        },
        {
            text: t("global_serviceName_imageEnhancement"),
            link: "/image-enhancement",
            image: {
                src: "https://revi.rvstatic.com/aihomedesign/images-v3/Landings/general/tools-IE.webp",
            },
            id: "itemremoval-othertoolsection-iecard",
        },
        {
            text: t("global_serviceName_dayToDusk"),
            link: "/day-to-dusk",
            image: {
                src: "https://revi.rvstatic.com/aihomedesign/images-v3/Landings/general/tools-DD.webp",
            },
            id: "itemremoval-othertoolsection-ddcard",
        },
    ];

    const personalSpacesSection = {
        title: t("landingComponents_customerComment_title"),
        items: [
            {
                title: t("landingComponents_customerComment_title_1"),
                subtitle: t("landingComponents_customerComment_desc_1"),
            },
            {
                title: t("landingComponents_customerComment_title_2"),
                subtitle: t("landingComponents_customerComment_desc_2"),
            },
            {
                title: t("landingComponents_customerComment_title_3"),
                subtitle: t("landingComponents_customerComment_desc_3"),
            },
        ],
    };

    const faqFirstRow: IAccordionItem[] = [
        {
            title: t("pages_IR_faq_q1"),
            content: t("pages_IR_faq_a1"),
        },
        {
            title: t("pages_IR_faq_q2"),
            content: t("pages_IR_faq_a2"),
        },
        {
            title: t("pages_IR_faq_q3"),
            content: t("pages_IR_faq_a3"),
        },
    ];

    const faqSecondRow: IAccordionItem[] = [
        {
            title: t("pages_IR_faq_q4"),
            content: t("pages_IR_faq_a4"),
        },
        {
            title: t("pages_IR_faq_q5"),
            content: t("pages_IR_faq_a5"),
        },
        {
            title: t("pages_IR_faq_q6"),
            content: t("pages_IR_faq_a6"),
        },
    ];

    const aboutService: IAboutService = {
        title: t("pages_IR_about_title"),
        description: useDevice().isDesktop
            ? [t("pages_IR_about_desc_1"), t("pages_IR_about_desc_2")]
            : [t("pages_IR_about_desc_1")],
        moreDescription: useDevice().isDesktop ? [] : [t("pages_IR_about_desc_2")],
    };
    const purpleCta = {
        title: t("pages_IR_purple_cta_title"),
    };

    return {
        heroContent,
        aboutItems,
        optionSection,
        personalSpacesSection,
        faqFirstRow,
        faqSecondRow,
        aboutService,
        itemRemovalSlides,
        compareSlider,
        purpleCta,
    };
};
